// src/convoGuide.js
//
// ═══════════════════════════════════════════════════════════════
// ON FILE — WHAT WE KNOW about a lead (Reece, 10/10/26)
// ═══════════════════════════════════════════════════════════════
// Reece asked for "some sort of label when I pull up the lead" — a likely
// fit, "more of a guide or thought, because in reality they could switch to
// something else" — and the facts behind it: "mortgage amount, mortgage date,
// if there's a second mortgage, maybe when they bought the property, if it was
// listed for sale". Then, twice: "don't make it fields that I would have to
// enter", and "I don't need guidance on what to ask … more so, just notes from
// any information that I already have that may be helpful".
//
// So this is READ-ONLY and only says what the CRM already holds. No talking
// points, no questions, no pitch rules, no suggestions, no inputs. Nothing
// here writes, tags, filters, picks a deal type or blocks anything.
//
//   leadFactsFor(lead, ctx) → {
//     fit:   {key, label, from, tag} | null   — "Likely fit: … — a guide,
//                                              could change" (fitLabelText)
//     lines: [{key, label, text, note?, group, warn?}]   — one line each,
//                                              only what is known
//     property                                 — the parsed facts (for the
//                                              card's own chips: factsCover)
//   }
//   ctx: { now: Date (for "N yrs ago"), loans: the files in memory (the
//          linked file), outbound: outboundCounts(rows) | null (not looked
//          up → no line), skipFileId: the file already on screen }
//
// The fit: a `fit:*` tag (fit:hs2 / fit:ftr / fit:hecm-refi / fit:prop-refi)
// wins; else the mail list's `class:*` tag (homesafe_second → HS2, ftr,
// refi_hecm, refi_prop). No tag → null: no label.
//
// lines, group "property" (in this order): first mortgage (amount · rate ·
// date (N yrs ago) · type · lender) — or the recorded reverse lien on a refi
// lead — second mortgage / "No second mortgage", bought (date for $X, owned N
// yrs), listed for sale (note: "per PropStream, may be out of date" — a fact
// only, never a tag, status or filter; MLS stays out of the CRM; PropStream's
// "Sold" record of their OWN purchase is left out), pre-
// foreclosure, liens, est. value + equity + LTV (combined with a second),
// owner occupied, property type · year built, the litigator flag.
// lines, group "history": do not contact (Outbound's rule: the flag, status
// DNC / Opt-Out Requested, an opt-out tag), the pipeline file (stage · last
// priced), the lead's own last pricing run, letters mailed (count · last date
// · last batch), responded (date · method), follow-up (the FILE's once it
// owns the call back — lib.fileOwnsCallBack), skip-traced contacts Outbound
// holds (COUNTS only — numbers, how many on the Public DNC list, emails; no
// "callable" claim; never a number or an address here).
// notesWithoutFacts(notes, lead): the list notes minus the lines the block
// already shows (New Call's History uses it).
// The card shows the first COLLAPSED_LINES + the litigator / do-not-contact
// lines (collapsedFacts); "Show all" opens the rest.
//
// Where the property facts come from, in order of trust (whole objects — a
// PropStream first mortgage is never mixed with an older list's lender):
//   1. profile meta `property` (lib.leadMetaFrom(lead.history).property),
//      written by the PropStream list sync — {firstMortgage|first|loan1:
//      {balance, date, rate, rateType, loanType|type, lender}, secondMortgage
//      |second|loan2: {…} | null, openLoans, purchase: {date, recordedDate,
//      amount, amountEstimated}, listing: {status, date, amount},
//      preForeclosure: {type, date}, lien | liens, ownerOccupied,
//      propertyType, yearBuilt, estValue}; flat PropStream keys (loan1Balance,
//      lastSaleDate, mlsStatus, preFcRecordType, lienType…) are read too.
//   2. the readable notes lines the sync writes — "First mortgage:",
//      "Second mortgage:", "Purchased:", "Listed for sale (per PropStream…):",
//      "Pre-foreclosure:", "Lien:", "Owner occupied:" (+ "Open loans:",
//      "Property type:", "Year built:", "Est. value:") — a line in the
//      sync's exact wording is read into the meta's shape and drawn the same
//      way (one date style on a card); any other wording is shown as written.
//   3. a list lead's own columns — loan_balance, rate, loan_type,
//      original_lender, origination_date / close_year, home_value. (A call-in
//      lead's columns are what was typed on the card — already on screen.)
// On a reverse-refi lead loan_balance is the RECORDED lien, never what they
// owe (lib.hasRecordedLien) — the line says so.

import { leadMetaFrom, hasRecordedLien, convertedLoanFor, fileOwnsCallBack } from './lib';

export var ON_FILE_TITLE="On file — what we know";
export var LISTING_NOTE="per PropStream, may be out of date";
export var COLLAPSED_LINES=4;
export var FIT_NAMES={hs2:"HomeSafe Second",ftr:"First-time reverse","hecm-refi":"HECM refi","prop-refi":"Proprietary refi"};
var FIT_ALIASES={
  "hs2":"hs2","hs-2":"hs2","homesafe-second":"hs2","homesafe-2nd":"hs2","homesafe2":"hs2",
  "ftr":"ftr","first-time":"ftr","first-time-reverse":"ftr",
  "hecm-refi":"hecm-refi","refi-hecm":"hecm-refi","hecmrefi":"hecm-refi",
  "prop-refi":"prop-refi","refi-prop":"prop-refi","proprietary-refi":"prop-refi","proprefi":"prop-refi",
};
var CLASS_FIT={homesafe_second:"hs2",ftr:"ftr",refi_hecm:"hecm-refi",refi_prop:"prop-refi"};
var LITIGATOR_TAGS=["tcpa-litigator","litigator"];
var OPT_OUT_TAGS=["opt-out","optout-request"];   // Outbound's DNC_TAGS

// ── The likely fit: fit:* wins over class:* ───────────────────────────
export function fitOf(lead){
  var tags=(lead&&Array.isArray(lead.tags))?lead.tags:[];
  var i,s;
  for(i=0;i<tags.length;i++){
    s=String(tags[i]==null?"":tags[i]).trim().toLowerCase();
    if(s.indexOf("fit:")===0){var f=FIT_ALIASES[s.slice(4).trim().replace(/[_\s]+/g,"-")];if(f)return{key:f,label:FIT_NAMES[f],from:"fit",tag:String(tags[i])};}
  }
  for(i=0;i<tags.length;i++){
    s=String(tags[i]==null?"":tags[i]).trim().toLowerCase();
    if(s.indexOf("class:")===0){var c=CLASS_FIT[s.slice(6).trim().replace(/[-\s]+/g,"_")];if(c)return{key:c,label:FIT_NAMES[c],from:"class",tag:String(tags[i])};}
  }
  return null;
}
// "Likely fit: HomeSafe Second — a guide, could change"
export function fitLabelText(fit){return fit?"Likely fit: "+fit.label+" — a guide, could change":"";}

// ── Readers (numbers / dates / words), tolerant of how a list writes them ──
function str(v){return v==null?"":String(v).trim();}
function blank(v){return v==null||(typeof v==="string"&&!v.trim());}
// Money: 402000 · "402,000" · "$402,000.00" · "$402K" · "$1.25M". null when none.
export function moneyIn(v){
  if(blank(v))return null;
  if(typeof v==="number")return isFinite(v)?v:null;
  var m=/^\s*~?\$?\s*(-?\d[\d,]*(?:\.\d+)?|-?\.\d+)\s*([kKmM])?\s*$/.exec(String(v));
  if(!m)return null;
  var n=parseFloat(m[1].replace(/,/g,""));if(!isFinite(n))return null;
  if(m[2])n*=/m/i.test(m[2])?1e6:1e3;
  return n;
}
// The first "$…" amount inside free text (a notes line).
function moneyInText(s){
  var m=/\$\s?(\d[\d,]*(?:\.\d+)?)\s*([KkMm](?![A-Za-z]))?/.exec(String(s||""));
  if(!m)return null;
  var n=parseFloat(m[1].replace(/,/g,""));if(!isFinite(n))return null;
  if(m[2])n*=/m/i.test(m[2])?1e6:1e3;
  return n;
}
// A rate: 2.875 · "2.875" · "2.875%" · 0.02875 (a fraction). Sane range only.
export function rateIn(v){
  if(blank(v))return null;
  var n=typeof v==="number"?v:parseFloat(String(v).replace(/[%\s]/g,""));
  if(!isFinite(n)||n<=0)return null;
  if(n<0.25)n=n*100;
  return n>0&&n<30?n:null;
}
function rateInText(s){var m=/(\d{1,2}(?:\.\d{1,4})?)\s*%/.exec(String(s||""));return m?rateIn(m[1]):null;}
function rateTypeIn(v){
  var s=str(v).toLowerCase();if(!s)return"";
  if(/\b(fixed)\b/.test(s))return"fixed";
  if(/\b(adj|adjustable|arm|variable|var)\b/.test(s))return"adjustable";
  return"";
}
function boolIn(v){
  if(v===true||v===false)return v;
  var s=str(v).toLowerCase();if(!s)return null;
  if(/^(y|yes|true|1|owner[- ]?occupied)\b/.test(s))return true;
  if(/^(n|no|false|0|absentee|not)\b/.test(s))return false;
  return null;
}
function intIn(v){if(blank(v))return null;var n=parseInt(String(v).replace(/[^0-9-]/g,""),10);return isFinite(n)?n:null;}
var MONTHS=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
var MONTH_RE="(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";
function monthOf(w){return MONTHS.map(function(x){return x.toLowerCase();}).indexOf(String(w).slice(0,3).toLowerCase())+1;}
function okDate(y,m,d){
  if(!(y>=1900&&y<=2100))return null;
  if(m!=null&&!(m>=1&&m<=12))return null;
  if(d!=null&&!(d>=1&&d<=31))return null;
  var o={y:y};if(m)o.m=m;if(m&&d)o.d=d;return o;
}
function yy(n){var y=+n;if(y>=100)return y;var cur=new Date().getFullYear()%100;return y<=cur+1?2000+y:1900+y;}
// A date: Date · Excel serial · a year · "2021-03-12" · a full ISO time (its
// LOCAL day) · "3/12/2021" · "3/12/21" · "03/2019" · "2019-03" · "Mar 2021" ·
// "March 12, 2021". → {y, m?, d?} (only the precision that was given) or null.
export function dateIn(v){
  if(blank(v))return null;
  if(v instanceof Date)return isNaN(v.getTime())?null:{y:v.getFullYear(),m:v.getMonth()+1,d:v.getDate()};
  if(typeof v==="number"){
    if(v>=1900&&v<=2100)return{y:Math.round(v)};
    if(v>20000&&v<80000){var t=new Date(Date.UTC(1899,11,30)+Math.round(v)*864e5);return{y:t.getUTCFullYear(),m:t.getUTCMonth()+1,d:t.getUTCDate()};}
    return null;
  }
  var s=String(v).trim();
  if(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)){var dt=new Date(s);if(!isNaN(dt.getTime()))return dateIn(dt);}
  if(/^\d{5}(\.\d+)?$/.test(s))return dateIn(parseFloat(s));
  return dateInText(s);
}
// The first date anywhere in free text, with the exact text it matched.
function dateInText(s){
  s=String(s||"");
  var r,o;
  if((r=/\b(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ][\d:.]+Z?)?\b/.exec(s))&&(o=okDate(+r[1],+r[2],+r[3])))return Object.assign(o,{text:r[0]});
  if((r=/\b(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})\b/.exec(s))&&(o=okDate(yy(r[3]),+r[1],+r[2])))return Object.assign(o,{text:r[0]});
  if((r=/\b(\d{1,2})\/(\d{4})\b/.exec(s))&&(o=okDate(+r[2],+r[1])))return Object.assign(o,{text:r[0]});
  if((r=/\b(\d{4})-(\d{1,2})\b/.exec(s))&&(o=okDate(+r[1],+r[2])))return Object.assign(o,{text:r[0]});
  if((r=new RegExp("\\b"+MONTH_RE+"\\.?\\s+(\\d{1,2}),?\\s+(\\d{4})\\b","i").exec(s))&&(o=okDate(+r[3],monthOf(r[1]),+r[2])))return Object.assign(o,{text:r[0]});
  if((r=new RegExp("\\b"+MONTH_RE+"\\.?,?\\s+(\\d{4})\\b","i").exec(s))&&(o=okDate(+r[2],monthOf(r[1]))))return Object.assign(o,{text:r[0]});
  if((r=/(?:^|[^\d$,.])((?:19|20)\d{2})(?![\d,%])/.exec(s))&&(o=okDate(+r[1])))return Object.assign(o,{text:r[1]});
  return null;
}
// Strip money and rates first so "$402,000" or "2.875%" is never read as a date.
function dateInLine(s){return dateInText(String(s||"").replace(/\$\s?\d[\d,]*(?:\.\d+)?\s*[KkMm]?/g," ").replace(/\d{1,2}(?:\.\d{1,4})?\s*%/g," "));}

// ── Formatting ──
export function fmtMoney(n){var v=Math.round(Number(n)||0);return(v<0?"−$":"$")+Math.abs(v).toLocaleString("en-US");}
// "$402K" · "$4.5K" · "$1.25M" · "$950"
export function fmtMoneyShort(n){
  var v=Math.abs(Number(n)||0),sg=Number(n)<0?"−":"";
  if(v>=1e6)return sg+"$"+String(+(v/1e6).toFixed(2))+"M";
  if(v>=1e4)return sg+"$"+Math.round(v/1e3)+"K";
  if(v>=1e3)return sg+"$"+String(+(v/1e3).toFixed(1))+"K";
  return sg+"$"+Math.round(v);
}
function fmtRate(r){return String(+Number(r).toFixed(3))+"%";}
export function fmtDate(d){
  if(!d)return"";
  if(d.m&&d.d)return MONTHS[d.m-1]+" "+d.d+", "+d.y;
  if(d.m)return MONTHS[d.m-1]+" "+d.y;
  return String(d.y);
}
function monthsSince(d,now){
  var ny=now.getFullYear(),nm=now.getMonth()+1,nd=now.getDate();
  if(!d.m)return(ny-d.y)*12;
  var mo=(ny-d.y)*12+(nm-d.m);
  if(d.d&&nd<d.d)mo--;
  return mo;
}
// "5 yrs ago" · "8 mos ago" · "this month" (year-only dates count whole years)
export function agoText(d,now){
  if(!d)return"";
  var mo=monthsSince(d,now||new Date());
  if(mo<0)return"";
  if(!d.m){var y=Math.round(mo/12);return y<=0?"this year":y===1?"1 yr ago":y+" yrs ago";}
  if(mo<1)return"this month";
  if(mo<12)return mo===1?"1 mo ago":mo+" mos ago";
  var yrs=Math.floor(mo/12);return yrs===1?"1 yr ago":yrs+" yrs ago";
}
function ownedText(d,now){
  var mo=monthsSince(d,now);if(mo<0)return"";
  if(!d.m){var y=Math.round(mo/12);return y<=0?"bought this year":"owned "+(y===1?"1 yr":y+" yrs");}
  if(mo<12)return"owned "+(mo<=1?"under 2 mos":mo+" mos");
  var yrs=Math.floor(mo/12);return"owned "+(yrs===1?"1 yr":yrs+" yrs");
}
// "(owned 12 yrs)" — folded into a "(recorded)" / "(est.)" the text already
// ends with, never a second bracket: "Mar 12, 2004 (recorded, owned 22 yrs)",
// "… for ~$650,000 (est., owned 12 yrs)".
function withOwned(t,ow){
  if(!ow)return t;
  return /\((recorded|est\.)\)$/.test(t)?t.slice(0,-1)+", "+ow+")":t+" ("+ow+")";
}
function pct(x){return Math.round(x*100)+"%";}
function plural(n,one,many){return n+" "+(n===1?one:(many||one+"s"));}

// ── Property facts ─────────────────────────────────────────────────────
function pick(o,keys){if(!o||typeof o!=="object")return undefined;for(var i=0;i<keys.length;i++){var v=o[keys[i]];if(!blank(v))return v;}return undefined;}
function has(o,keys){if(!o||typeof o!=="object")return false;for(var i=0;i<keys.length;i++){if(Object.prototype.hasOwnProperty.call(o,keys[i]))return true;}return false;}
var NONE_RE=/^\s*(none|no|n\/a|na|-|—|no second(?: mortgage)?|none (?:on record|listed)|not listed|0)\s*\.?\s*$/i;
// ── The list sync's own notes wording (scratch ftr1009/lib.js notesLines) ──
// "$402,000 @ 2.875% (Fixed) · New Conventional · Wells Fargo · 3/12/2021",
// "6/1/2014 (recorded) for ~$650,000 (est.)", "Active 9/1/2026 $1,250,000",
// "State Tax Lien 5/4/2019 $12,345.67". A line in EXACTLY that shape is read
// into the profile meta's shape and drawn the same way ("$402,000 · 2.875%
// fixed · Mar 12, 2021 (5 yrs ago) · …"), so the 98.6% of the 10/9 list that
// carries only the notes reads like the rest — one date style on a card
// (review F5). `raw` is kept (factsCover reads it); `fromSync` says draw it
// from the parts. Any other wording is shown as written.
var MDY_RE=/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
function mdyIn(s){var m=MDY_RE.exec(str(s));if(!m)return null;var o=okDate(+m[3],+m[1],+m[2]);return o&&o.d?Object.assign(o,{text:m[0]}):null;}
function amountOf(s){var n=parseFloat(String(s).replace(/,/g,""));return isFinite(n)?n:null;}
var SYNC_LOAN_HEAD=/^(?:\$(\d[\d,]*(?:\.\d+)?)|balance not listed)(?: @ (\d{1,2}(?:\.\d+)?)%)?(?: \(([^()]+)\))?$/;
// PropStream's "Rate Type" words — only those in the bracket (a typed "(approx)" stays as written).
var SYNC_RATE_TYPES=/^(fixed|fixed rate|variable|adjustable|adjustable rate|arm|other|unknown|multiple loan amounts|step interest rate)$/i;
var SYNC_OPEN_LOANS=/^\d+ open loans \(PropStream lists \d+\)$/i;
function syncLoan(raw){
  var pieces=raw.split(" · "),h=SYNC_LOAN_HEAD.exec(pieces[0]);
  if(!h||(h[3]&&!SYNC_RATE_TYPES.test(h[3])))return null;
  var date=null,mid=[],i,p;
  for(i=1;i<pieces.length;i++){
    p=pieces[i];
    if(!p||p!==p.trim())return null;
    if(SYNC_OPEN_LOANS.test(p)&&i===pieces.length-1)break;   // the count is drawn from "Open loans:"
    if(date)return null;                                      // nothing else follows the date
    var d=mdyIn(p);
    if(d){date=d;continue;}
    if(/\$\s?\d|\d\/\d{1,2}\/\d|%/.test(p))return null;
    mid.push(p);
  }
  // A lender may carry a year ("MORTGAGE 2000 INC") when the date follows it;
  // with no date piece, a year in the text is the date — as written then.
  if(mid.length>2||(!date&&mid.some(function(x){return !!dateInText(x);})))return null;
  var bal=h[1]!=null?amountOf(h[1]):null;
  // The sync writes the loan type before the lender; drawn in that order, so
  // one piece reads the same whichever it is (raw still answers factsCover).
  return{balance:bal!=null&&bal>0?bal:null,rate:h[2]!=null?rateIn(h[2]):null,rateType:rateTypeIn(h[3]),date:date,type:mid[0]||"",lender:mid[1]||""};
}
// "<type> <m/d/yyyy> $<amount>" (each part optional; a date or an amount must
// be there — else there is nothing to re-draw).
var SYNC_EVENT=/^(.*?)(?:(?:^| )(\d{1,2}\/\d{1,2}\/\d{4}))?(?:(?:^| )\$(\d[\d,]*(?:\.\d+)?))?$/;
function syncEvent(raw,withAmount){
  var m=SYNC_EVENT.exec(raw);if(!m)return null;
  var type=m[1].trim(),d=m[2]?mdyIn(m[2]):null,amt=m[3]!=null?amountOf(m[3]):null;
  if((m[2]&&!d)||(!d&&amt==null))return null;
  if(amt!=null&&!withAmount)return null;
  if(/\$\s?\d|\d\/\d{1,2}\/\d/.test(type))return null;
  return{type:type,date:d,amount:amt};
}
// "6/1/2014 for $650,000" · "3/12/2004 (recorded)" · "… for ~$650,000 (est.)" · "for $650,000"
var SYNC_PURCHASE=/^(?:(\d{1,2}\/\d{1,2}\/\d{4})( \(recorded\))?)?(?:(?:^| )for (~)?\$(\d[\d,]*(?:\.\d+)?)( \(est\.\))?)?$/;
function syncPurchase(raw){
  var m=SYNC_PURCHASE.exec(raw);if(!m)return null;
  var d=m[1]?mdyIn(m[1]):null,amt=m[4]!=null?amountOf(m[4]):null;
  if((m[1]&&!d)||(!d&&amt==null))return null;
  if(amt!=null&&amt<=0)amt=null;
  return{date:d,recorded:!!(d&&m[2]),amount:amt,estimated:amt!=null&&!!(m[3]||m[5])};
}
// One loan off a notes line: the sync's shape → its parts; otherwise keep the
// line as written (shown verbatim) and read the amount / rate / date out of it.
function loanFromText(s){
  var raw=str(s);if(!raw)return undefined;
  if(NONE_RE.test(raw)||/^no\s+(second|2nd|first|1st)?\s*mortgage\b/i.test(raw)||/^none\b/i.test(raw))return null;
  var sy=syncLoan(raw);
  if(sy)return Object.assign(sy,{raw:raw,fromSync:true});
  return{raw:raw,balance:moneyInText(raw),rate:rateInText(raw),rateType:rateTypeIn((/\b(fixed|adjustable|arm|variable)\b/i.exec(raw)||[])[0]),date:dateInLine(raw)};
}
// One loan off the profile meta (object, "none" string, or null = none).
function loanFromMeta(o){
  if(o===undefined)return undefined;
  if(o===null||o===false||o===0)return null;
  if(typeof o==="string")return loanFromText(o);
  if(typeof o!=="object")return undefined;
  if(o.none===true||o.exists===false||o.has===false)return null;
  var L={
    balance:moneyIn(pick(o,["balance","amount","bal","loanBalance","estBalance","loanAmount","originalAmount"])),
    date:dateIn(pick(o,["date","loanDate","recordingDate","recorded","originated","originationDate","origDate"])),
    rate:rateIn(pick(o,["rate","interestRate"])),
    rateType:rateTypeIn(pick(o,["rateType","rate_type"])),
    type:str(pick(o,["type","loanType","loan_type"])),
    lender:str(pick(o,["lender","lenderName","originalLender"])),
  };
  if(L.balance!=null&&L.balance<=0)L.balance=null;
  return(L.balance!=null||L.date||L.rate!=null||L.type||L.lender)?L:undefined;
}
function flatLoan(p,pre){
  var cap=function(k){return pre+k;};
  if(!has(p,[cap("Balance"),cap("Date"),cap("Rate"),cap("Type"),cap("Lender")]))return undefined;
  return loanFromMeta({balance:p[cap("Balance")],date:p[cap("Date")],rate:p[cap("Rate")],rateType:p[cap("RateType")],type:p[cap("Type")],lender:p[cap("Lender")]});
}
function firstOf(){for(var i=0;i<arguments.length;i++){if(arguments[i]!==undefined)return arguments[i];}return undefined;}
function eventFrom(o,dateKeys,amountKeys,typeKeys){
  if(o==null||typeof o!=="object")return undefined;
  var e={date:dateIn(pick(o,dateKeys)),amount:moneyIn(pick(o,amountKeys)),type:str(pick(o,typeKeys))};
  return(e.date||e.amount!=null||e.type)?e:undefined;
}
// An event off a notes line ("<type> <m/d/yyyy> $<amount>" → its parts, else
// as written). A pre-foreclosure has no amount (withAmount false).
function eventFromText(s,withAmount){
  var raw=str(s);if(!raw||NONE_RE.test(raw))return undefined;
  var sy=syncEvent(raw,withAmount!==false);
  if(sy)return Object.assign(sy,{raw:raw,fromSync:true});
  return{raw:raw,date:dateInLine(raw),amount:moneyInText(raw)};
}
function purchaseFromText(s){
  var raw=str(s);if(!raw||NONE_RE.test(raw))return undefined;
  var sy=syncPurchase(raw);
  if(sy)return Object.assign(sy,{raw:raw,fromSync:true});
  return{raw:raw,date:dateInLine(raw),amount:moneyInText(raw)};
}
// The purchase off the meta: the sale date, else its recording date (said so);
// "amountEstimated" = PropStream's estimate ("~$650,000 (est.)").
function purchaseFrom(o){
  if(o==null||typeof o!=="object")return undefined;
  var sale=dateIn(pick(o,["date","saleDate","lastSaleDate","purchaseDate"]));
  var rec=sale?null:dateIn(pick(o,["recordedDate","recordingDate","lastSaleRecordingDate"]));
  var amt=moneyIn(pick(o,["amount","price","saleAmount","lastSaleAmount","purchasePrice","purchaseAmount"]));
  if(amt!=null&&amt<=0)amt=null;
  var e={date:sale||rec,recorded:!!rec,amount:amt,estimated:amt!=null&&(o.amountEstimated===true||o.estimated===true)};
  return(e.date||e.amount!=null)?e:undefined;
}
var NOTE_RE={
  first:/^[ \t]*(?:first mortgage|1st mortgage)[ \t]*:[ \t]*(.+)$/im,
  second:/^[ \t]*(?:second mortgage|2nd mortgage)[ \t]*:[ \t]*(.+)$/im,
  purchase:/^[ \t]*(?:purchased|bought|last sale)[ \t]*:[ \t]*(.+)$/im,
  listing:/^[ \t]*listed for sale[^:\n]*:[ \t]*(.+)$/im,
  preFc:/^[ \t]*pre-?foreclosure[ \t]*:[ \t]*(.+)$/im,
  lien:/^[ \t]*liens?[ \t]*:[ \t]*(.+)$/gim,
  owner:/^[ \t]*owner[- ]occupied[ \t]*:[ \t]*(.+)$/im,
  openLoans:/^[ \t]*(?:total[ \t]+)?open loans[ \t]*:[ \t]*(\d+)/im,
  propertyType:/^[ \t]*property type[ \t]*:[ \t]*(.+)$/im,
  yearBuilt:/^[ \t]*year built[ \t]*:[ \t]*(\d{4})/im,
  estValue:/^[ \t]*est(?:imated|\.)?[ \t]*value[ \t]*:[ \t]*(.+)$/im,
};
function noteLine(notes,re){var m=re.exec(notes);return m?m[1].trim():undefined;}
// A lead off a mailing list (its columns came off the list), not a call-in
// whose columns are what was typed on the card.
function isListLead(lead){
  var t=(lead&&lead.tags)||[];
  if(t.some(function(x){return /^(class|sl|list):/i.test(String(x||""));}))return true;
  var src=str(lead&&lead.list_source);
  return !!src&&!/call|web|intake|statement|portal|referral|handwritten/i.test(src);
}
// A "Sold" listing within 120 days of the purchase is that purchase (both
// dates need at least a month to tell).
function dayNum(d){return Date.UTC(d.y,d.m-1,d.d||15)/864e5;}
function soldNearPurchase(listing,purchase){
  if(!listing||!purchase||!listing.date||!purchase.date||!listing.date.m||!purchase.date.m)return false;
  if(!/^\s*sold\b/i.test(str(listing.type)||str(listing.raw)))return false;
  return Math.abs(dayNum(listing.date)-dayNum(purchase.date))<=120;
}
// The whole facts record for a lead — every field optional.
export function propertyFactsFor(lead){
  if(!lead)return null;
  var meta=leadMetaFrom(lead.history)||{};
  var P=(meta.property&&typeof meta.property==="object")?meta.property:{};
  var notes=String(lead.notes||"");
  var lienLead=hasRecordedLien(lead);
  var listLead=isListLead(lead);

  // First mortgage: meta → notes → a list lead's own columns.
  var first=firstOf(loanFromMeta(firstOf(P.firstMortgage,P.first,P.loan1,P.mortgage1)),flatLoan(P,"loan1"));
  var firstSrc="meta";
  if(first===undefined){var t1=noteLine(notes,NOTE_RE.first);if(t1!==undefined){first=loanFromText(t1);firstSrc="notes";}}
  if(first===undefined&&listLead&&!lienLead){
    var col={balance:moneyIn(lead.loan_balance),rate:rateIn(lead.rate),rateType:"",type:str(lead.loan_type),lender:str(lead.original_lender||lead.lender),date:dateIn(lead.origination_date||lead.close_year)};
    if(col.balance!=null&&col.balance<=0)col.balance=null;
    if(col.balance!=null||col.rate!=null||col.type||col.lender||col.date){first=col;firstSrc="columns";}
  }

  // Second mortgage: meta (null = none) → notes → the open-loan count.
  var secondKeys=["secondMortgage","second","loan2","mortgage2"];
  var second=firstOf(loanFromMeta(firstOf(P.secondMortgage,P.second,P.loan2,P.mortgage2)),flatLoan(P,"loan2"));
  if(second===undefined&&has(P,secondKeys)){var sv=firstOf(P.secondMortgage,P.second,P.loan2,P.mortgage2);if(sv===null||sv===false||NONE_RE.test(str(sv)))second=null;}
  if(second===undefined&&P.hasSecond===false)second=null;
  var t2;
  if(second===undefined){t2=noteLine(notes,NOTE_RE.second);if(t2!==undefined)second=loanFromText(t2);}
  var openLoans=intIn(firstOf(pick(P,["openLoans","totalOpenLoans","open_loans"]),noteLine(notes,NOTE_RE.openLoans)));
  if(openLoans==null&&t2){var ol=/(\d+)\s+open loans/i.exec(t2);if(ol)openLoans=+ol[1];}
  if(second===undefined&&openLoans!=null&&openLoans<=1&&(first||openLoans===0))second=null;
  if(first===undefined&&openLoans===0)first=null;

  var purchase=firstOf(purchaseFrom(firstOf(P.purchase,P.lastSale,P.sale)),purchaseFrom(P));
  if(purchase===undefined)purchase=purchaseFromText(noteLine(notes,NOTE_RE.purchase));

  var listing=firstOf(eventFrom(firstOf(P.listing,P.listed,P.mls),["date","listDate","statusDate"],["amount","price","listPrice"],["status"]),
    eventFrom(P,["mlsDate","listingDate"],["mlsAmount","listingAmount","listPrice"],["mlsStatus","listingStatus"]));
  if(listing===undefined){var tl=noteLine(notes,NOTE_RE.listing);if(tl!==undefined)listing=eventFromText(tl.replace(/\(?\s*per propstream[^)]*\)?/i,"").replace(/\s*[—–-]\s*$/,""));}
  if(listing&&listing.amount!=null&&listing.amount<=0)listing=Object.assign({},listing,{amount:null});
  // PropStream's MLS record of the owner's OWN purchase is not a listing: the
  // 10/9 sync marks it "Sold (their purchase)" + matchesPurchase (2,368 of its
  // 3,002 "Sold" records), and any "Sold" dated within 120 days of the
  // purchase is the same sale. No "Listed for sale" line for it — the Bought
  // line already says it.
  var lsrc=firstOf(P.listing,P.listed,P.mls);
  var listingIsPurchase=!!(listing&&((lsrc&&typeof lsrc==="object"&&lsrc.matchesPurchase===true)
    ||/their purchase/i.test(str(listing.type)+" "+str(listing.raw))||soldNearPurchase(listing,purchase)));
  if(listingIsPurchase)listing=null;

  var preFc=firstOf(eventFrom(firstOf(P.preForeclosure,P.preforeclosure,P.preFc),["date","recordingDate"],[],["type","recordType"]),
    eventFrom(P,["preFcDate","preFcRecordingDate","preForeclosureDate"],[],["preFcType","preFcRecordType","preForeclosureType"]));
  if(preFc===undefined)preFc=eventFromText(noteLine(notes,NOTE_RE.preFc),false);

  var liens=[];
  var lp=firstOf(P.liens,P.lien);
  (Array.isArray(lp)?lp:lp?[lp]:[]).forEach(function(x){var e=typeof x==="string"?eventFromText(x):eventFrom(x,["date","recordingDate"],["amount"],["type"]);if(e)liens.push(e);});
  if(!liens.length){var fl=eventFrom(P,["lienDate"],["lienAmount"],["lienType"]);if(fl)liens.push(fl);}
  if(!liens.length){var lm,lre=new RegExp(NOTE_RE.lien.source,"gim");while((lm=lre.exec(notes))){var le=eventFromText(lm[1]);if(le)liens.push(le);}}
  liens.forEach(function(l){if(l.amount!=null&&l.amount<=0)l.amount=null;});

  var ownerOcc=boolIn(firstOf(pick(P,["ownerOccupied","owner_occupied","ownerOcc"]),noteLine(notes,NOTE_RE.owner)));
  var propertyType=str(firstOf(pick(P,["propertyType","property_type"]),noteLine(notes,NOTE_RE.propertyType)));
  var yearBuilt=intIn(firstOf(pick(P,["yearBuilt","year_built"]),noteLine(notes,NOTE_RE.yearBuilt)));
  if(yearBuilt!=null&&!(yearBuilt>1700&&yearBuilt<=2100))yearBuilt=null;
  var value=moneyIn(pick(P,["estValue","value","homeValue","est_value"]));
  if(value==null){var tv=noteLine(notes,NOTE_RE.estValue);if(tv!==undefined)value=moneyInText(tv)!=null?moneyInText(tv):moneyIn(tv);}
  if((value==null||value<=0)&&listLead&&moneyIn(lead.home_value)>0)value=moneyIn(lead.home_value);
  if(value!=null&&value<=0)value=null;

  return{
    first:first===undefined?undefined:first,firstSrc:first?firstSrc:null,second:second,openLoans:openLoans,
    reverseLien:lienLead?{amount:moneyIn(lead.loan_balance)>0?moneyIn(lead.loan_balance):null,
      date:dateIn(lead.origination_date||lead.close_year),lender:str(lead.original_lender||lead.lender),type:str(lead.loan_type)}:null,
    owedTyped:lienLead&&moneyIn(meta.curBalance)>0?moneyIn(meta.curBalance):null,
    purchase:purchase||null,listing:listing||null,listingIsPurchase:listingIsPurchase,preForeclosure:preFc||null,liens:liens,
    ownerOccupied:ownerOcc,propertyType:propertyType,yearBuilt:yearBuilt,value:value,
    litigator:hasLitigatorTag(lead),
  };
}
// A TCPA litigator lead: the app's `tcpa-litigator` tag or the list sync's
// `litigator` (New Call's header flag reads it too — review LF-5).
export function hasLitigatorTag(lead){
  var tags=lead&&Array.isArray(lead.tags)?lead.tags:[];
  return tags.some(function(t){return LITIGATOR_TAGS.indexOf(str(t).toLowerCase())>=0;});
}

// ── The property lines ──
function loanText(L,now){
  if(L.raw&&!L.fromSync){
    var t=L.raw;
    if(L.date&&L.date.text&&!/\bago\b/i.test(t)){var a=agoText(L.date,now);if(a)t=t.replace(L.date.text,L.date.text+" ("+a+")");}
    return t;
  }
  var parts=[];
  if(L.balance!=null)parts.push(fmtMoney(L.balance));
  if(L.rate!=null)parts.push(fmtRate(L.rate)+(L.rateType?" "+L.rateType:""));
  else if(L.rateType)parts.push(L.rateType+" rate");
  if(L.date){var ag=agoText(L.date,now);parts.push(fmtDate(L.date)+(ag?" ("+ag+")":""));}
  if(L.type)parts.push(L.type);
  if(L.lender)parts.push(L.lender);
  return parts.join(" · ");
}
function eventText(e,withAmount){
  if(e.raw&&!e.fromSync)return e.raw;
  var bits=[];
  if(e.type)bits.push(e.type);
  if(e.date)bits.push(fmtDate(e.date));
  var s=bits.join(" · ");
  if(withAmount&&e.amount!=null)s+=(s?" · ":"")+fmtMoney(e.amount);
  return s;
}
// What's owed on record, for equity / LTV. A reverse lien is never a balance:
// only a balance typed off their statement (meta curBalance) counts there.
function owedOf(F){
  if(F.reverseLien)return F.owedTyped!=null?{total:F.owedTyped,second:0,free:false}:null;
  if(F.first===null&&F.second===null)return{total:0,second:0,free:true};
  if(!F.first||F.first.balance==null)return null;
  var sec=F.second&&F.second.balance!=null?F.second.balance:0;
  return{total:F.first.balance+sec,second:sec,free:false};
}
export function propertyLines(F,now){
  now=now||new Date();
  var out=[];
  if(!F)return out;
  var add=function(key,label,text,extra){if(text)out.push(Object.assign({key:key,label:label,text:text,group:"property"},extra||{}));};
  if(F.reverseLien){
    var R=F.reverseLien,rb=[];
    if(R.amount!=null)rb.push(fmtMoney(R.amount)+" recorded lien");
    if(R.date){var ra=agoText(R.date,now);rb.push(fmtDate(R.date)+(ra?" ("+ra+")":""));}
    if(R.type)rb.push(R.type);
    if(R.lender)rb.push(R.lender);
    add("reverse","Reverse mortgage",rb.join(" · "),R.amount!=null?{note:"the recorded lien, not what they owe"}:null);
    if(F.owedTyped!=null)add("owed","Balance (typed)",fmtMoney(F.owedTyped));
  }else{
    if(F.first)add("first","First mortgage",loanText(F.first,now));
    else if(F.first===null)add("first","First mortgage","None on record");
    var many=F.openLoans!=null&&F.openLoans>=2;
    // A notes line shown as written may already end "· 3 open loans
    // (PropStream lists 2)" — never say it twice. (The sync's own line is
    // drawn from its parts, without that ending — the count is said here.)
    if(F.second)add("second","Second mortgage",loanText(F.second,now)+(F.openLoans!=null&&F.openLoans>2&&!(F.second.raw&&!F.second.fromSync&&/open loans/i.test(F.second.raw))?" · records show "+F.openLoans+" open loans":""));
    else if(F.second===null&&many)add("second","Second mortgage","Not listed — records show "+F.openLoans+" open loans");
    else if(F.second===null&&F.first!==null)add("second","Second mortgage","No second mortgage");
    else if(F.second===undefined&&many)add("second","Second mortgage","Not listed — records show "+F.openLoans+" open loans");
  }
  if(F.purchase){
    var P=F.purchase,pt;
    if(P.raw&&!P.fromSync){pt=P.raw;if(P.date&&P.date.text&&!/\bowned\b/i.test(pt))pt=withOwned(pt,ownedText(P.date,now));}
    else{
      var amt=P.amount!=null?(P.estimated?"~"+fmtMoney(P.amount)+" (est.)":fmtMoney(P.amount)):"";
      pt=(P.date?fmtDate(P.date)+(P.recorded?" (recorded)":""):"")+(amt?(P.date?" for ":"For ")+amt:"");
      if(P.date)pt=withOwned(pt,ownedText(P.date,now));
    }
    add("purchase","Bought",pt);
  }
  if(F.listing){
    var Lg=F.listing,lt;
    if(Lg.raw&&!Lg.fromSync)lt=Lg.raw;
    else{var lb=[];if(Lg.type)lb.push(Lg.type);if(Lg.date)lb.push(fmtDate(Lg.date));lt=lb.join(" · ")+(Lg.amount!=null?(lb.length?" at ":"At ")+fmtMoney(Lg.amount):"");}
    add("listing","Listed for sale",lt||"On record",{note:LISTING_NOTE,warn:true});
  }
  if(F.preForeclosure)add("preForeclosure","Pre-foreclosure",eventText(F.preForeclosure,false)||"On record",{warn:true});
  F.liens.forEach(function(l,i){add("lien"+i,"Lien",eventText(l,true)||"On record",{warn:true});});
  if(F.value!=null){
    var ow3=owedOf(F),vt=fmtMoney(F.value);
    if(ow3&&ow3.free)vt+=" · no loans on record";
    else if(ow3&&ow3.total>0){
      vt+=" · ~"+fmtMoneyShort(F.value-ow3.total)+" equity · "+pct(ow3.total/F.value)+(ow3.second>0?" combined LTV":" LTV");
    }
    add("value","Est. value",vt);
  }
  if(F.ownerOccupied===true)add("ownerOccupied","Owner occupied","Yes");
  else if(F.ownerOccupied===false)add("ownerOccupied","Owner occupied","No (per PropStream)",{warn:true});
  var pp=[];if(F.propertyType)pp.push(F.propertyType);if(F.yearBuilt)pp.push("built "+F.yearBuilt);
  if(pp.length)add("property","Property",pp.join(" · "));
  if(F.litigator)add("litigator","Litigator flag","TCPA litigator on the skip-trace list — kept off the Outbound dialer",{warn:true});
  return out;
}

// ── The lead's own history ─────────────────────────────────────────────
function dayKey(d){return d?d.y+"-"+(d.m||0)+"-"+(d.d||0):"";}
function cmpDay(a,b){return(a.y-b.y)||((a.m||0)-(b.m||0))||((a.d||0)-(b.d||0));}
function mailingDate(m){
  if(!m)return null;
  var d=dateIn(m.sentAt);
  if(d)return d;
  var s=str(m.dateSent);
  if(!s)return null;
  // A piped "<as typed> | <settled date>" — the settled one is the last.
  if(s.indexOf("|")>=0)s=s.slice(s.lastIndexOf("|")+1);
  // A year alone ("2026", "~2026") is no date to show.
  var x=dateIn(s);
  return x&&x.m?x:null;
}
// Letters on record: one per mailing id, and two records on the same day are
// one letter (a sync's copy). → {count, last:{y,m,d?}|null, batch}
export function mailingSummary(lead){
  var arr=Array.isArray(lead&&lead.mailings)?lead.mailings:[];
  var ids={},days={},count=0,last=null,lastM=null;
  arr.forEach(function(m,i){
    if(!m||typeof m!=="object")return;
    var id=str(m.id)||("#"+i);
    if(ids[id])return;ids[id]=1;
    var d=mailingDate(m);
    if(d){var k=dayKey(d);if(days[k])return;days[k]=1;}
    count++;
    if(d&&(!last||cmpDay(d,last)>=0)){last=d;lastM=m;}
  });
  if(!lastM&&count){for(var j=arr.length-1;j>=0;j--){if(arr[j]&&typeof arr[j]==="object"){lastM=arr[j];break;}}}
  var batch=lastM?str(lastM.batch)||str(lastM.campaign)||str(lastM.dm_campaign):"";
  return{count:count,last:last,batch:batch};
}
// The skip-traced contacts Outbound holds for a lead → COUNTS only: numbers,
// how many of them are on the Public DNC list (flagged on any row), emails,
// and whether a row is flagged litigator. No "callable" claim — whether a
// number is dialed is Outbound's call (it also drops DNC / opted-out /
// litigator people, litigator numbers, dead lines and wrong numbers), and a
// count here that said otherwise contradicted the "Do not contact" line.
export function outboundCounts(rows){
  var arr=Array.isArray(rows)?rows:[];
  var nums={},dnc={},emails={},lit=false;
  arr.forEach(function(r){
    if(!r||typeof r!=="object")return;
    if(r.litigator===true)lit=true;
    var ph=Array.isArray(r.phones)?r.phones:[];
    ph.forEach(function(p){
      var d=String((p&&typeof p==="object"?(p.n||p.number||p.phone):p)||"").replace(/\D/g,"");
      if(d.length===11&&d.charAt(0)==="1")d=d.slice(1);
      if(d.length!==10)return;
      nums[d]=1;
      if(p&&typeof p==="object"){if(p.dnc)dnc[d]=1;if(p.litigator)lit=true;}
    });
    if(!ph.length&&Array.isArray(r.phone_digits))r.phone_digits.forEach(function(x){var d=String(x||"").replace(/\D/g,"").slice(-10);if(d.length===10)nums[d]=1;});
    (Array.isArray(r.emails)?r.emails:[]).forEach(function(e){var s=String((e&&typeof e==="object"?(e.email||e.e):e)||"").trim().toLowerCase();if(/^[^@\s]+@[^@\s]+$/.test(s))emails[s]=1;});
  });
  var n=Object.keys(nums).length;
  var onDnc=Object.keys(nums).filter(function(d){return !!dnc[d];}).length;
  return{rows:arr.length,phones:n,dnc:onDnc,emails:Object.keys(emails).length,litigator:lit};
}
function outboundText(o,leadIsLitigator){
  if(!o.rows||(!o.phones&&!o.emails))return"None on file";
  var bits=[];
  if(o.phones)bits.push(plural(o.phones,"phone number")+(o.dnc?" ("+o.dnc+" on the Public DNC list)":""));
  if(o.emails)bits.push(plural(o.emails,"email"));
  if(o.litigator&&!leadIsLitigator)bits.push("flagged litigator in Outbound");
  return bits.join(" · ");
}
// The newest pricing run on a record: a pitched scenario (scenarioHistory) or
// a priced-not-pitched run (quoteLog) — whichever is newer.
function lastPriced(scenarios,quotes){
  var best=null;
  (Array.isArray(scenarios)?scenarios:[]).forEach(function(s){
    if(!s)return;
    var t=Date.parse(s.timestamp||"")||0,d=dateIn(s.timestamp)||dateIn(s.date);
    var what=str(s.product)+(s.manual?" (entered by hand)":s.lender?" ("+str(s.lender)+")":"");
    if(!best||t>=best.t)best={t:t,day:d,what:what,pitched:true};
  });
  (Array.isArray(quotes)?quotes:[]).forEach(function(q){
    if(!q)return;
    var t=Number(q.epoch)||Date.parse(q.at||"")||0,d=t?dateIn(new Date(t)):dateIn(q.date);
    var b=(q.top||[])[0];
    var what=b?"best "+str(b.name)+(b.lender?" ("+str(b.lender)+")":""):"";
    if(!best||t>best.t)best={t:t,day:d,what:what,pitched:false};
  });
  return best;
}
function pricedText(p){return p?[p.day?fmtDate(p.day):"",p.pitched?(p.what?"pitched "+p.what:"pitched"):p.what].filter(Boolean).join(" — "):"";}
export function historyLines(lead,ctx){
  ctx=ctx||{};
  var out=[];
  if(!lead)return out;
  var meta=leadMetaFrom(lead.history)||{};
  var add=function(key,label,text,extra){if(text)out.push(Object.assign({key:key,label:label,text:text,group:"history"},extra||{}));};
  // Outbound's rule (leadHold): the flag, status DNC / "Opt-Out Requested",
  // or an opt-out tag. Only the flag / DNC status also keeps the lead out of
  // Marketing's mailing queue, so the wording says which.
  var dncTags=(Array.isArray(lead.tags)?lead.tags:[]).map(function(t){return str(t).toLowerCase();});
  var st=str(lead.status).toLowerCase();
  if(lead.doNotContact||lead.do_not_contact||st==="dnc")
    add("dnc","Do not contact","Opted out — off mailings & Outbound",{warn:true});
  else if(st==="opt-out requested"||OPT_OUT_TAGS.some(function(t){return dncTags.indexOf(t)>=0;}))
    add("dnc","Do not contact","Opt-out requested — off Outbound",{warn:true});
  var file=Array.isArray(ctx.loans)?convertedLoanFor(lead,ctx.loans):null;
  if(file&&file.id!==ctx.skipFileId){
    var ft=[file.id,str(file.stage)].filter(Boolean).join(" · ");
    if(file.falloutReason)ft+=" ("+str(file.falloutReason)+")";
    var fp=lastPriced(file.scenarioHistory,file.quoteLog);
    if(fp&&pricedText(fp))ft+=" · last priced "+pricedText(fp);
    add("file","Pipeline file",ft);
  }else if(!file){
    var lp=lastPriced(null,meta.quoteLog);
    if(lp&&pricedText(lp))add("priced","Last priced",pricedText(lp));
  }
  var ms=mailingSummary(lead);
  add("mailed","Letters mailed",ms.count?[plural(ms.count,"letter"),ms.last?(ms.count>1?"last ":"")+fmtDate(ms.last):"",ms.batch].filter(Boolean).join(" · "):"None yet");
  if(lead.responded||lead.responded_at){
    var rd=dateIn(lead.responded_at);
    add("response","Responded",[rd?fmtDate(rd):"Yes",str(lead.response_method)].filter(Boolean).join(" · "));
  }
  // Follow-up. Once the lead is a file that owns the call back
  // (lib.fileOwnsCallBack — the rule Follow-ups / the bell / the Prospect
  // lane use), the lead's own date is stale: on that file's card it's left
  // out (the card has the file's date), elsewhere the FILE's date is shown.
  var owns=!!file&&fileOwnsCallBack(lead,ctx.loans);
  if(!owns){
    var fu=dateIn(lead.follow_up_date);
    if(fu)add("followUp","Follow-up",fmtDate(fu)+(str(meta.followUpNote)?" — "+str(meta.followUpNote):""));
  }else if(file.id!==ctx.skipFileId){
    var ffu=dateIn(file.followUpDate);
    if(ffu)add("followUp","Follow-up",fmtDate(ffu)+(str(file.followUpNote)?" — "+str(file.followUpNote):""),{note:"on "+file.id});
  }
  var leadLit=hasLitigatorTag(lead);
  if(ctx.outbound&&typeof ctx.outbound==="object")add("outbound","Skip-traced contacts",outboundText(ctx.outbound,leadLit),ctx.outbound.rows&&(ctx.outbound.phones||ctx.outbound.emails)?{note:"in Outbound"}:null);
  return out;
}

// The lines the card shows before "Show all": the first COLLAPSED_LINES
// (first, second, bought, listed — what Reece asked for) plus, wherever they
// fall, the compliance lines — the litigator flag and do not contact. DNC /
// litigator awareness stays (nothing is a hard stop), so they're never behind
// "Show all" (review LF-5). In the lines' own order.
var ALWAYS_SHOWN={litigator:1,dnc:1};
export function collapsedFacts(lines){
  var arr=Array.isArray(lines)?lines:[];
  return arr.filter(function(l,i){return i<COLLAPSED_LINES||!!(l&&ALWAYS_SHOWN[l.key]);});
}

// ── Everything on file, in the order the card shows it ─────────────────
export function leadFactsFor(lead,ctx){
  ctx=ctx||{};
  if(!lead)return{fit:null,lines:[],property:null};
  var now=ctx.now instanceof Date?ctx.now:new Date();
  var F=propertyFactsFor(lead);
  return{fit:fitOf(lead),lines:propertyLines(F,now).concat(historyLines(lead,ctx)),property:F};
}

// The list notes WITHOUT the lines this block already shows — so New Call's
// History ("Imported / list notes") doesn't print every fact a second time in
// another format, or a second, differently worded "Likely fit". A line goes
// only when the block shows that fact (the label's first line — every Lien
// line; "Property facts from:" with any property line; "Likely fit:" with the
// fit chip); anything else in the notes stays, as written.
var NOTE_LABELS=[
  [/^[ \t]*(?:first mortgage|1st mortgage)[ \t]*:/i,"first",["first"]],
  [/^[ \t]*(?:second mortgage|2nd mortgage)[ \t]*:/i,"second",["second","first"]],
  [/^[ \t]*(?:total[ \t]+)?open loans[ \t]*:/i,"openLoans",["second","first"]],
  [/^[ \t]*(?:purchased|bought|last sale)[ \t]*:/i,"purchase",["purchase"]],
  [/^[ \t]*listed for sale[^:\n]*:/i,"listing",["listing","purchase"]],
  [/^[ \t]*pre-?foreclosure[ \t]*:/i,"preFc",["preForeclosure"]],
  [/^[ \t]*liens?[ \t]*:/i,"lien",["lien0"],true],
  [/^[ \t]*owner[- ]occupied[ \t]*:/i,"owner",["ownerOccupied"]],
  [/^[ \t]*property type[ \t]*:/i,"propertyType",["property"]],
  [/^[ \t]*year built[ \t]*:/i,"yearBuilt",["property"]],
  [/^[ \t]*est(?:imated|\.)?[ \t]*value[ \t]*:/i,"estValue",["value"]],
];
var SOURCE_NOTE_RE=/^[ \t]*property facts from[ \t]*:/i;
var FIT_NOTE_RE=/^[ \t]*likely fit[ \t]*:/i;
export function notesWithoutFacts(notes,lead){
  var s=notes==null?"":String(notes);
  if(!s.trim()||!lead)return s;
  var R=leadFactsFor(lead);
  var shown={};R.lines.forEach(function(l){shown[l.key]=1;});
  var anyProp=R.lines.some(function(l){return l.group==="property";});
  if(!anyProp&&!R.fit)return s;
  var seen={},dropped=0;
  var kept=s.split(/\r?\n/).filter(function(line){
    if(FIT_NOTE_RE.test(line)){if(R.fit&&!seen.fit){seen.fit=1;dropped++;return false;}return true;}
    if(SOURCE_NOTE_RE.test(line)){if(anyProp){dropped++;return false;}return true;}
    for(var i=0;i<NOTE_LABELS.length;i++){
      var L=NOTE_LABELS[i];
      if(!L[0].test(line))continue;
      var isShown=L[2].some(function(k){return !!shown[k];});
      if(!isShown)return true;
      if(L[3]){dropped++;return false;}
      if(seen[L[1]])return true;
      seen[L[1]]=1;dropped++;return false;
    }
    return true;
  });
  if(!dropped)return s;
  return kept.join("\n").replace(/\n{3,}/g,"\n\n").trim();
}

// Which of the card's own "On file" chips the first-mortgage line already
// says (the same value), so the card doesn't print it twice. A value that
// differs — a correction made on the card — is never hidden.
// chips: {rate, lender, origDate, loanType} → {rate, lender, origDate, loanType}: bool
export function factsCover(facts,chips){
  var out={rate:false,lender:false,origDate:false,loanType:false};
  var F=facts&&facts.property;
  if(!F||!chips)return out;
  var L=F.reverseLien?{rate:null,lender:F.reverseLien.lender,date:F.reverseLien.date,type:F.reverseLien.type}:F.first;
  if(!L)return out;
  var same=function(a,b){return !!str(a)&&str(a).toLowerCase()===str(b).toLowerCase();};
  var inRaw=function(v){return !!(L.raw&&str(v)&&L.raw.toLowerCase().indexOf(str(v).toLowerCase())>=0);};
  var r=rateIn(chips.rate);
  out.rate=r!=null&&L.rate!=null&&Math.abs(r-L.rate)<0.0005;
  out.lender=same(chips.lender,L.lender)||inRaw(chips.lender);
  out.loanType=same(chips.loanType,L.type)||inRaw(chips.loanType);
  var d=dateIn(chips.origDate);
  out.origDate=!!(d&&L.date&&d.y===L.date.y&&(!d.m||!L.date.m||d.m===L.date.m)&&(!d.d||!L.date.d||d.d===L.date.d));
  return out;
}
